import { defineStore } from "pinia";
import { computed, ref } from "vue";
import { useRest } from "@/composables/useRest";

const getBaseUrl = () => `${process.env.VUE_APP_BACKEND_ENVIRONMENT}/files/archivopdf`;

export const useBibliotecaStore = defineStore("biblioteca", () => {
  const documentos = ref([]);
  const filterNombre = ref("");
  const filterTipo = ref([]);
  const isLoading = ref(null);
  const isLoadingTipos = ref(null);
  const tiposDocumentos = ref([]);
  const remoteTotalDocs = ref(0);
  const useRemotePagination = ref(false);
  const activeSearchQuery = ref(null);

  const currentPage = ref(1);
  const pageSize = ref(7);

  const rest = useRest();

  const filteredArchivos = computed(() => {
    if (!filterNombre.value && (!filterTipo.value || filterTipo.value.length === 0)) {
      return documentos.value;
    }

    return documentos.value.filter((doc) => {
      const coincideNombre =
        !filterNombre.value || doc.Nombre?.toLowerCase().includes(filterNombre.value.toLowerCase());
      const coincideTipo =
        !filterTipo.value || filterTipo.value.length === 0 || filterTipo.value.includes(doc.Tipo);

      return coincideNombre && coincideTipo;
    });
  });

  const totalDocs = computed(() =>
    useRemotePagination.value ? remoteTotalDocs.value : filteredArchivos.value.length
  );
  const totalPages = computed(() => Math.ceil(totalDocs.value / pageSize.value));
  const offset = computed(() => (currentPage.value - 1) * pageSize.value);

  const paginatedArchivos = computed(() => {
    if (useRemotePagination.value) return documentos.value;

    const start = offset.value;
    const end = start + pageSize.value;
    return filteredArchivos.value.slice(start, end);
  });

  const setDocumentos = async () => {
    try {
      isLoading.value = true;
      const { data } = await rest.get(`${getBaseUrl()}?offset=0`);
      documentos.value = data.data || [];
      useRemotePagination.value = false;
      activeSearchQuery.value = null;
      currentPage.value = 1;
    } catch (e) {
      console.error("setDocumentos >>", e);
    } finally {
      isLoading.value = false;
    }
  };

  const getTotal = async (searchQuery = null) => {
    try {
      let url = `${getBaseUrl()}?getTotal=true`;
      if (searchQuery) url += `&search=${encodeURIComponent(searchQuery)}`;

      const { data } = await rest.get({ url });
      remoteTotalDocs.value = +(data?.doc?.total || 0);
    } catch (e) {
      console.error("getTotal documentos >>", e);
    }
  };

  const setDocumentosList = async (page = 1) => {
    try {
      isLoading.value = true;
      useRemotePagination.value = true;
      activeSearchQuery.value = null;
      currentPage.value = page;

      const offsetValue = (page - 1) * pageSize.value;
      const url = `${getBaseUrl()}?limit=${pageSize.value}&offset=${offsetValue}`;
      const { data } = await rest.get({ url });
      documentos.value = data.data || [];
    } catch (e) {
      console.error("setDocumentosList >>", e);
    } finally {
      isLoading.value = false;
    }
  };

  const searchDocumentos = async (searchQuery, page = 1) => {
    try {
      isLoading.value = true;
      useRemotePagination.value = true;
      activeSearchQuery.value = searchQuery;
      currentPage.value = page;

      const offsetValue = (page - 1) * pageSize.value;
      const url =
        `${getBaseUrl()}?limit=${pageSize.value}&offset=${offsetValue}` +
        `&search=${encodeURIComponent(searchQuery)}`;
      const { data } = await rest.get({ url });
      documentos.value = data.data || [];
    } catch (e) {
      console.error("searchDocumentos >>", e);
    } finally {
      isLoading.value = false;
    }
  };

  const resolveCurrentPage = () => {
    const maxPage = Math.max(1, Math.ceil(remoteTotalDocs.value / pageSize.value) || 1);
    return Math.min(currentPage.value, maxPage);
  };

  const refreshCurrentView = async () => {
    if (!useRemotePagination.value) {
      await setDocumentos();
      return;
    }

    await getTotal(activeSearchQuery.value);
    const targetPage = resolveCurrentPage();

    if (activeSearchQuery.value) {
      await searchDocumentos(activeSearchQuery.value, targetPage);
      return;
    }

    await setDocumentosList(targetPage);
  };

  const createDocumento = async ({ Nombre, Tipo, Detalle = "", file }) => {
    try {
      const formData = new FormData();
      formData.append("Nombre", Nombre);
      formData.append("Tipo", Tipo);
      formData.append("Detalle", Detalle);
      formData.append("userfile[]", file, file.name);

      const { data } = await rest.post({
        url: getBaseUrl(),
        formData,
      });

      await refreshCurrentView();
      return data;
    } catch (e) {
      console.error("createDocumento >>", e);
      throw e;
    }
  };
    const updateDocumentoconFile = async ({ Nombre, Tipo, Detalle = "", file }, id) => {
    try {
      // console.log("updateDocumentoconFile >> Response data:",id);
      const formData = new FormData();
      formData.append("Nombre", Nombre);
      formData.append("Tipo", Tipo);
      formData.append("Detalle", Detalle);
      formData.append("userfile[]", file, file.name);

      const { data } = await rest.post({
        url: getBaseUrl(),
        formData,
      });
      

      await deleteDocumento(id);
      await refreshCurrentView();
      return data;
    } catch (e) {
      console.error("updateDocumentoconFile >>", e);
      throw e;
    }
  };

const updateDocumento = async (
  id,
  { Nombre, Tipo, Detalle = "", Filename, Extension, file }
) => {
  console.log("updateDocumento >> Payload recibido:", {
    id,
    Nombre,
    Tipo,
    Detalle,
    Filename,
    Extension,
    file,
  });
  try {
    const formData = new FormData();

    formData.append("Nombre", Nombre);
    formData.append("Tipo", Tipo);
    formData.append("Detalle", Detalle);

    if (Filename) formData.append("Filename", Filename);
    if (Extension) formData.append("Extension", Extension);

    if (file) {
      formData.append("userfile[]", file, file.name);
    }
for (const [key, value] of formData.entries()) {
  console.log("FormData item:", key, value);
}
    const { data } = await rest.put({
      url: `${getBaseUrl()}/${id}`,
      formData,
    });

    await refreshCurrentView();
    return data;
  } catch (e) {
    console.error("updateDocumento >>", e);
    throw e;
  }
};

  const deleteDocumento = async (id) => {
    try {
      const { data } = await rest.del({
        url: `${getBaseUrl()}/${id}`,
      });

      await refreshCurrentView();
      return data;
    } catch (e) {
      console.error("deleteDocumento >>", e);
      throw e;
    }
  };

  const setTiposDocumentos = async () => {
    try {
      isLoadingTipos.value = true;
      const { data } = await rest.get(
        `${process.env.VUE_APP_BACKEND_ENVIRONMENT}/parametros/categoriatipoarchivopdf`
      );
      tiposDocumentos.value = data.data || [];
    } catch (e) {
      console.error("setTiposDocumentos >>", e);
    } finally {
      isLoadingTipos.value = false;
    }
  };

  const goToPage = (page) => {
    if (page < 1 || page > totalPages.value) return;

    if (useRemotePagination.value) {
      if (activeSearchQuery.value) {
        searchDocumentos(activeSearchQuery.value, page);
        return;
      }

      setDocumentosList(page);
      return;
    }

    currentPage.value = page;
  };

  const nextPage = () => {
    if (currentPage.value < totalPages.value) {
      goToPage(currentPage.value + 1);
    }
  };

  const prevPage = () => {
    if (currentPage.value > 1) {
      goToPage(currentPage.value - 1);
    }
  };

  const handlePageChange = ({ page, rows }) => {
    pageSize.value = rows;

    if (useRemotePagination.value) {
      goToPage(page + 1);
      return;
    }

    currentPage.value = page + 1;
  };

  const filterDocs = (nombre = "", tipos = []) => {
    const normalizedNombre = String(nombre || "").trim();

    filterNombre.value = normalizedNombre;
    filterTipo.value = Array.isArray(tipos)
      ? tipos.filter((tipo) => String(tipo || "").trim())
      : [];
    useRemotePagination.value = false;
    activeSearchQuery.value = null;
    currentPage.value = 1;
  };

  return {
    documentos,
    totalDocs,
    isLoading,
    currentPage,
    pageSize,
    totalPages,
    offset,
    paginatedArchivos,
    filterNombre,
    filterTipo,
    filteredArchivos,
    tiposDocumentos,
    getTotal,
    setDocumentos,
    setDocumentosList,
    searchDocumentos,
    nextPage,
    prevPage,
    goToPage,
    handlePageChange,
    filterDocs,
    setTiposDocumentos,
    createDocumento,
    updateDocumentoconFile,
    updateDocumento,
    deleteDocumento,
  };
});
